import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

/**
 * Task-first training passport. One tile per required subject, one contextual
 * upload dialog; never send applicants to provider checkout pages.
 *
 * The backend owns certificate extraction, review, evidence deduplication,
 * expiry and work-readiness decisions. The UI only reports upload receipt.
 */
export default function TrainingCertificateGrid({
  trainings = [],
  allTrainings = [],
  onUpload,
  uploading = null,
  feedback = null,
}) {
  const [selected, setSelected] = useState(null);
  const [savedLocally, setSavedLocally] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const getCode = item => String(item?.id || item?.code || '').trim();
  const requirementIdFor = item => `training_${getCode(item)}${item?.status === 'due_soon' ? '_renewal' : ''}`;
  const checking = allTrainings.filter(item =>
    ['awaiting_review', 'processing', 'completed', 'needs_information'].includes(item.status)
  ).length;
  const verified = allTrainings.filter(item => item.status === 'verified').length;
  const statusName = {
    missing: 'Required',
    expired: 'Expired',
    due_soon: 'Renew soon',
    rejected: 'Needs correction',
    needs_information: 'More evidence needed',
  };
  const actionName = item => {
    if (item.status === 'missing') return 'Add certificate';
    if (item.status === 'rejected' || item.status === 'needs_information') return 'Correct evidence';
    return 'Renew certificate';
  };

  const handleFile = async event => {
    const file = event.target.files?.[0];
    const target = selected;
    if (!file || !target || submitting) return;
    setSubmitting(true);
    try {
      const confirmed = await onUpload(target.requirementId, file);
      if (confirmed === true) {
        setSavedLocally(old => ({ ...old, [target.requirementId]: true }));
        setSelected(null);
      }
    } finally {
      // Supports choosing the exact same filename after a failed attempt.
      event.target.value = '';
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="applicant-training-primary-actions">
      <p className="text-xs text-slate-600">
        Tap a subject to add your certificate. One certificate can cover several
        subjects — upload it once and our team checks the rest.
      </p>
      {checking > 0 && (
        <p role="status" data-testid="training-evidence-under-review"
          className="rounded-xl bg-teal-50 px-3 py-2 text-xs text-teal-900">
          Certificate evidence is being checked. No repeat upload is needed.
        </p>
      )}
      {trainings.length > 0 ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="training-needed-list">
          {trainings.map((training, index) => {
            const code = getCode(training);
            const requirementId = requirementIdFor(training);
            const received = savedLocally[requirementId] === true;
            const busy = uploading === requirementId || (submitting && selected?.requirementId === requirementId);
            const allowed = /^[a-z0-9_]{2,100}$/.test(code);
            const status = received ? 'Being checked' : statusName[training.status] || 'Required';
            const statusClass = received ? 'bg-teal-100 text-teal-800' :
              ['expired', 'rejected'].includes(training.status) ? 'bg-red-100 text-red-800' :
              training.status === 'due_soon' ? 'bg-amber-100 text-amber-800' :
              'bg-slate-100 text-slate-700';
            return (
              <button type="button" key={code || index}
                data-testid={`training-row-${code}`}
                disabled={!allowed || busy || received}
                onClick={() => setSelected({
                  name: training.name || training.title || 'Training',
                  status: training.status, rejectionReason: training.rejection_reason,
                  requirementId, code,
                })}
                className={`flex min-h-32 flex-col items-start justify-between gap-3 rounded-xl border p-3 text-left
                  ${received ? 'border-teal-200 bg-teal-50' : 'border-slate-200 bg-white hover:border-teal-500'}
                  disabled:cursor-default`}>
                <span className="block">
                  <span className="block text-sm font-semibold leading-snug text-slate-900">
                    {training.name || training.title || 'Training'}
                  </span>
                  <span className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusClass}`}>
                    {status}
                  </span>
                </span>
                <span className="text-xs font-semibold text-teal-800">
                  {received ? 'Received' : busy ? 'Uploading…' : actionName(training)}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="rounded-xl bg-teal-50 px-3 py-3 text-sm text-teal-900"
          data-testid="training-no-pending-uploads">
          No certificate needed from you right now.
        </p>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600"
        data-testid="training-status-overview">
        <span>{verified} verified</span>
        <span>{checking} checking</span>
        <span>Previous certificates: My records</span>
      </div>
      {feedback?.requirementId?.startsWith('training_') && (
        <p className={`rounded-lg border p-3 text-sm
          ${feedback.status === 'error'
            ? 'border-red-200 bg-red-50 text-red-900'
            : 'border-teal-200 bg-teal-50 text-teal-900'}`}
          role={feedback.status === 'error' ? 'alert' : 'status'}>
          {feedback.message}
        </p>
      )}
      <Dialog open={Boolean(selected)}
        onOpenChange={isOpen => { if (!isOpen && !submitting) setSelected(null); }}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="training-upload-dialog">
          <DialogHeader>
            <DialogTitle>{selected?.name || 'Training certificate'}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-700">
            {selected?.status === 'due_soon'
              ? 'This training is coming up for renewal. Your previous certificate stays in your history.'
              : selected?.status === 'expired'
              ? 'This training has expired. Add the new certificate after your refresher.'
              : selected?.status === 'rejected' || selected?.status === 'needs_information'
              ? 'Your team needs updated or clearer evidence for this subject.'
              : 'Add the certificate you already have for this subject.'}
          </p>
          {selected?.rejectionReason && (
            <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-900">
              {selected.rejectionReason}
            </p>
          )}
          <label className="block cursor-pointer rounded-xl border border-dashed border-teal-300 bg-teal-50 p-4 text-center text-sm font-semibold text-teal-900">
            {submitting ? 'Uploading certificate…' : 'Choose PDF or photo to upload'}
            <input aria-label="Choose training certificate" type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp" disabled={submitting}
              className="sr-only" onChange={handleFile} />
          </label>
          <p className="text-xs text-slate-600">
            PDF or image up to 10 MB. Your organisation will verify the evidence before it counts.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
